import React, { useContext, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../config/AuthContext";
import { Button, FloatButton, Table, Tag, message, Typography, Card } from "antd";
import { ColumnsType } from "antd/es/table";
import { BiRefresh } from "react-icons/bi";
import { FaEye } from "react-icons/fa";
import { BsCheck } from "react-icons/bs";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import BbddColors from "../../../../styles/BbddColors";
import { GetJBbddProblemesIdEnviament } from "../../../../dao/bbdd/GetJBbddProblemesIdEnviament";
import { PostBbddValidar } from "../../../../dao/bbdd/PostBbddValidar";
import Editor from "@monaco-editor/react";

const ProblemaIdAlumnes: React.FC = () => {
    const { isLoggedIn, user } = useContext(AuthContext);
    const { id_problema, id_activitat } = useParams();
    const [dades, setDades] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    useEffect(() => {
        if (isLoggedIn) checkDades();
    }, [id_problema, id_activitat]);

    async function checkDades() {
        setLoading(true);
        const data = await GetJBbddProblemesIdEnviament(id_problema + "", "0", id_activitat);
        setDades(data || []);
        setLoading(false);
    }

    async function handleCheck(ele: any) {
        setLoading(true);
        const res = await PostBbddValidar(ele.id_usuari, id_problema + "", id_activitat);
        if (res?.response === "OK") message.success("Validació correcta!");
        else message.error("Error en validar l'exercici.");
        await checkDades();
    }

    /** Subfila expandida (Editor SQL + Error) */
    const subRowRender = (record: any) => (
        <div
            style={{
                backgroundColor: "#fafafa",
                padding: "16px",
                borderRadius: "10px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
            }}
        >
            <div>
                <h5 style={{ marginBottom: 8, color: BbddColors.colors.color5 }}>
                    Resposta Alumne:
                </h5>
                <Editor
                    height="30vh"
                    defaultLanguage="sql"
                    value={record.actual || "-- Sense resposta --"}
                    theme="vs-light"
                    options={{ readOnly: true, minimap: { enabled: false } }}
                />
            </div>

            <div>
                <h5 style={{ marginBottom: 8, color: BbddColors.colors.color4 }}>
                    Solució Esperada:
                </h5>
                <Editor
                    height="30vh"
                    defaultLanguage="sql"
                    value={record.solucio || "-- Sense solució --"}
                    theme="vs-light"
                    options={{ readOnly: true, minimap: { enabled: false } }}
                />
            </div>

            {record.errors && (
                <div>
                    <h5 style={{ marginBottom: 8, color: "red" }}>Error complet:</h5>
                    <pre
                        style={{
                            background: "#fff5f5",
                            border: "1px solid #ffccc7",
                            borderRadius: "8px",
                            padding: "12px",
                            overflowX: "auto",
                        }}
                    >
                        {record.errors}
                    </pre>
                </div>
            )}
        </div>
    );

    /** Columnas principales */
    const mainColumns: ColumnsType<any> = [
        {
            key: "expand",
            width: 40,
            render: (_, record) =>
                record.actual || record.solucio ? (
                    <FaEye
                        style={{
                            cursor: "pointer",
                            fontSize: 16,
                            color: expandedRowKeys.includes(record.id_usuari)
                                ? BbddColors.colors.color4
                                : "gray",
                        }}
                        onClick={() => {
                            if (expandedRowKeys.includes(record.id_usuari))
                                setExpandedRowKeys([]);
                            else setExpandedRowKeys([record.id_usuari]);
                        }}
                    />
                ) : null,
        },
        {
            key: "usuari",
            title: "Nom",
            dataIndex: "usuari",
            render: (nom, record) => (
                <div>
                    <Typography.Text strong>{nom}</Typography.Text>
                    <div style={{ fontSize: 11, color: '#888' }}>ID: {record.id_usuari}</div>
                </div>
            )
        },
        {
            key: "moment",
            title: "Moment",
            dataIndex: "moment",
            render: (moment) => <Tag color={BbddColors.colors.color2}>{moment}</Tag>,
        },
        {
            key: "estat",
            title: "Estat",
            dataIndex: "estat",
            render: (estat) => (
                <Tag color={estat === 2 ? "green" : "red"}>
                    {estat === 2 ? "CORRECTE" : "INCORRECTE"}
                </Tag>
            ),
        },
        {
            key: "ordre",
            title: "Test #",
            dataIndex: "ordre",
            render: (ordre) => <Tag color={BbddColors.colors.color3}>#{ordre}</Tag>,
        },
        {
            key: "errors",
            title: "Errors (breu)",
            dataIndex: "errors",
            render: (err) => (
                <Tag color={err ? "red" : "green"}>
                    {err ? err.substring(0, 60) + (err.length > 60 ? "..." : "") : "Sense errors"}
                </Tag>
            ),
        },
        {
            key: "actions",
            title: "Accions",
            render: (_, record) =>
                record.estat !== 2 && (
                    <Button
                        icon={<BsCheck />}
                        type="primary"
                        style={{ backgroundColor: BbddColors.colors.color4 }}
                        onClick={() => handleCheck(record)}
                        title="Validar resposta"
                    />
                ),
        },
    ];

    return (
        <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Typography.Title level={2} style={{ marginBottom: 0 }}>Llista d’enviaments del problema</Typography.Title>
                    <Typography.Text type="secondary">Seguiment i validació de respostes</Typography.Text>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    {(user.tipus === "1" || user.tipus === "0") && (
                        <Button
                            type="primary"
                            icon={<BiRefresh />}
                            onClick={checkDades}
                            loading={loading}
                            style={{ background: AvaluacioColors.colors.color6, borderColor: AvaluacioColors.colors.color6 }}
                        >
                            Refrescar
                        </Button>
                    )}
                </div>
            </div>

            <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ overflow: 'hidden', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}>
                <Table
                    size="middle"
                    bordered={false}
                    loading={loading}
                    columns={mainColumns}
                    dataSource={dades}
                    rowKey="id_usuari"
                    expandable={{
                        expandedRowKeys,
                        expandIconColumnIndex: -1,
                        expandedRowRender: subRowRender,
                    }}
                    pagination={false}
                />
            </Card>
        </div>
    );
};

export default ProblemaIdAlumnes;
